import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, LogOut, Youtube, Instagram } from "lucide-react";
import { BrandLogo } from "../BrandLogo";
import { cn } from "../../lib/utils";
import { navLinks, siteMeta } from "../../pages/site/content";
import { useAuth } from "../../contexts/AuthContext";
import { ThemeToggle } from "../ThemeToggle";
import { SiteSeo } from "./SiteSeo";
import { TvModeProvider, useTvMode } from "../../lib/useTvMode";
import api from "../../services/api";

function NavItem({ item, onNavigate }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  if (!item.children?.length) {
    return (
      <NavLink
        to={item.href}
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            "text-sm font-medium transition-colors py-2",
            isActive ? "text-[#e0c15a]" : "text-white/85 hover:text-white"
          )
        }
      >
        {item.label}
      </NavLink>
    );
  }

  const childActive = item.children.some((c) => location.pathname === c.href);

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className={cn(
          "inline-flex items-center gap-1 text-sm font-medium transition-colors py-2",
          childActive || open ? "text-[#e0c15a]" : "text-white/85 hover:text-white"
        )}
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        aria-expanded={open}
      >
        {item.label}
        <ChevronDown className={cn("h-3.5 w-3.5 transition", open && "rotate-180")} />
      </button>
      {open && (
        <div
          className="absolute left-0 top-full pt-2 min-w-[220px] z-50"
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false);
          }}
        >
          <div className="rounded-xl bg-slate-950/95 border border-white/10 shadow-xl py-2 backdrop-blur-md">
            {item.children.map((child) => (
              <Link
                key={child.href}
                to={child.href}
                onClick={() => {
                  setOpen(false);
                  onNavigate?.();
                }}
                className="block px-4 py-3 text-sm text-white/80 hover:bg-white/5 hover:text-white focus-visible:bg-white/10 focus-visible:text-white"
              >
                {child.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MobileNavSection({ item, onNavigate }) {
  const [open, setOpen] = useState(false);
  const hasChildren = !!item.children?.length;

  if (!hasChildren) {
    return (
      <Link
        to={item.href}
        className="flex min-h-11 items-center py-3 text-white/90 font-medium"
        onClick={onNavigate}
      >
        {item.label}
      </Link>
    );
  }

  return (
    <div>
      <button
        type="button"
        className="flex w-full min-h-11 items-center justify-between py-3 text-white/90 font-medium"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {item.label}
        <ChevronDown className={cn("h-4 w-4 transition", open && "rotate-180")} />
      </button>
      {open && (
        <div className="pb-2 space-y-0.5">
          {item.children.map((child) => (
            <Link
              key={child.href}
              to={child.href}
              className="flex min-h-11 items-center pl-4 py-2.5 text-sm text-white/65"
              onClick={onNavigate}
            >
              {child.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isAuthenticated, homePath, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const handleLogout = async () => {
    setMobileOpen(false);
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-all duration-300 pt-[env(safe-area-inset-top)]",
        scrolled || mobileOpen
          ? "bg-[#060f42]/95 backdrop-blur-md border-b border-white/10 shadow-lg"
          : "bg-transparent"
      )}
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2 shrink-0 min-w-0">
          <BrandLogo imgClassName="h-9 w-9 sm:h-10 sm:w-10" />
          <span className="min-w-0">
            <span className="block text-white font-bold tracking-tight leading-none text-sm sm:text-base truncate">
              {siteMeta.name}
            </span>
            <span className="hidden sm:block text-[10px] uppercase tracking-[0.2em] text-white/55 mt-0.5">
              Sub Zone C
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {navLinks.map((item) => (
            <NavItem key={item.href + item.label} item={item} />
          ))}
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <ThemeToggle variant="brand" className="min-h-11 min-w-11 h-11 w-11" />
          {isAuthenticated ? (
            <>
              <Link
                to={homePath || "/profile"}
                className="hidden sm:inline-flex rounded-full bg-[#c9a227] text-[#1a1405] text-sm font-semibold px-4 py-2.5 hover:bg-[#e0c15a] transition"
              >
                Portal
              </Link>
              <button
                type="button"
                onClick={handleLogout}
                className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-white/25 text-white/90 text-sm font-medium px-3.5 py-2.5 hover:bg-white/10 transition min-h-11"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </button>
            </>
          ) : (
            <Link
              to="/login"
              className="hidden sm:inline-flex rounded-full bg-[#c9a227] text-[#1a1405] text-sm font-semibold px-4 py-2.5 hover:bg-[#e0c15a] transition"
            >
              Sign in
            </Link>
          )}
          <button
            type="button"
            className="lg:hidden inline-flex min-h-11 min-w-11 items-center justify-center text-white rounded-full hover:bg-white/10"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-white/10 bg-[#060f42] max-h-[min(80vh,calc(100dvh-3.5rem-env(safe-area-inset-top)))] overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom)]">
          <div className="px-4 py-3 space-y-1">
            {navLinks.map((item) => (
              <MobileNavSection
                key={item.href + item.label}
                item={item}
                onNavigate={() => setMobileOpen(false)}
              />
            ))}
            {isAuthenticated ? (
              <>
                <Link
                  to={homePath || "/profile"}
                  className="mt-3 flex min-h-11 items-center justify-center rounded-full bg-[#c9a227] text-[#1a1405] font-semibold"
                  onClick={() => setMobileOpen(false)}
                >
                  Open Portal
                </Link>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex w-full min-h-11 items-center justify-center gap-2 rounded-full border border-white/20 text-white/90 font-medium"
                >
                  <LogOut className="h-4 w-4" />
                  Sign out
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="mt-3 flex min-h-11 items-center justify-center rounded-full bg-[#c9a227] text-[#1a1405] font-semibold"
                onClick={() => setMobileOpen(false)}
              >
                Sign in
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

export function SiteFooter() {
  const [contact, setContact] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/public/contact");
        if (!cancelled && res.data.success) {
          setContact(res.data.data || null);
        }
      } catch {
        // Keep static siteMeta fallbacks.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const address = contact?.address || siteMeta.address;
  const email = contact?.email || siteMeta.email;
  const phone = contact?.phone || siteMeta.phone;
  const kingschatUrl = contact?.kingschat_url || null;
  const youtubeUrl = contact?.youtube_url || null;
  const instagramUrl = contact?.instagram_url || null;

  return (
    <footer className="bg-[#060f42] text-white/80 border-t border-white/10 pb-[env(safe-area-inset-bottom)]">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10 sm:py-14 grid gap-8 sm:gap-10 md:grid-cols-3">
        <div>
          <BrandLogo imgClassName="h-12 w-12 sm:h-14 sm:w-14 mb-4" className="mb-4" />
          <p className="font-bold text-white text-lg">{siteMeta.name}</p>
          <p className="text-sm text-white/55 mt-1 max-w-xs">{siteMeta.fullName}</p>
          <p className="text-sm mt-4 text-white/65 leading-relaxed">{siteMeta.tagline}</p>
          {(youtubeUrl || instagramUrl || kingschatUrl) && (
            <div className="mt-5 flex flex-wrap gap-2">
              {youtubeUrl ? (
                <a
                  href={youtubeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/15 transition"
                  aria-label="YouTube"
                >
                  <Youtube className="h-4 w-4" />
                </a>
              ) : null}
              {instagramUrl ? (
                <a
                  href={instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/15 transition"
                  aria-label="Instagram"
                >
                  <Instagram className="h-4 w-4" />
                </a>
              ) : null}
              {kingschatUrl ? (
                <a
                  href={kingschatUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/15 transition"
                  aria-label="KingsChat"
                >
                  <img
                    src="https://kingsch.at/h/css/images/favicon.ico"
                    alt=""
                    width={16}
                    height={16}
                    className="h-4 w-4 rounded-sm"
                  />
                </a>
              ) : null}
            </div>
          )}
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#e0c15a]/90 mb-3">
            Quick links
          </p>
          <ul className="space-y-0.5 text-sm">
            {navLinks.map((l) => (
              <li key={l.href}>
                <Link to={l.href} className="flex min-h-11 items-center hover:text-white transition">
                  {l.label}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/login" className="flex min-h-11 items-center hover:text-white transition">
                Member portal
              </Link>
            </li>
          </ul>
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#e0c15a]/90 mb-3">
            Contact
          </p>
          <ul className="space-y-3 text-sm">
            {address ? (
              <li className="whitespace-pre-line text-white/80">{address}</li>
            ) : null}
            {email ? (
              <li>
                <a href={`mailto:${email}`} className="hover:text-white break-all">
                  {email}
                </a>
              </li>
            ) : null}
            {phone ? (
              <li>
                <a
                  href={`tel:${String(phone).replace(/\s+/g, "")}`}
                  className="hover:text-white"
                >
                  {phone}
                </a>
              </li>
            ) : null}
            {youtubeUrl ? (
              <li>
                <a
                  href={youtubeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 hover:text-white"
                >
                  <Youtube className="h-3.5 w-3.5 shrink-0" />
                  YouTube
                </a>
              </li>
            ) : null}
            {instagramUrl ? (
              <li>
                <a
                  href={instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 hover:text-white"
                >
                  <Instagram className="h-3.5 w-3.5 shrink-0" />
                  Instagram
                </a>
              </li>
            ) : null}
            {kingschatUrl ? (
              <li className="pt-1">
                <a
                  href={kingschatUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-11 items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/15 transition"
                >
                  <img
                    src="https://kingsch.at/h/css/images/favicon.ico"
                    alt=""
                    width={18}
                    height={18}
                    className="h-[18px] w-[18px] rounded-sm"
                  />
                  Open KingsChat
                </a>
              </li>
            ) : null}
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-white/40 px-4">
        © {new Date().getFullYear()} {siteMeta.name}. All rights reserved.
      </div>
    </footer>
  );
}

function SiteLayoutInner() {
  const location = useLocation();
  const { tvMode, isLargeScreen, cinemaActive } = useTvMode();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div
      className={cn(
        "min-h-screen bg-background text-foreground font-sans antialiased site-shell overflow-x-hidden",
        tvMode && "site-shell--tv",
        isLargeScreen && "site-shell--wide",
        cinemaActive && "site-shell--cinema"
      )}
    >
      <SiteSeo />
      {!cinemaActive && <SiteHeader />}
      <main className={cn("min-w-0", cinemaActive && "min-h-dvh")}>
        <Outlet />
      </main>
      {!cinemaActive && <SiteFooter />}
    </div>
  );
}

export function SiteLayout() {
  return (
    <TvModeProvider>
      <SiteLayoutInner />
    </TvModeProvider>
  );
}
